import { createContext, type ReactNode, useContext } from 'react';
import { useStore } from 'zustand';
import type { DesktopShellState, DesktopShellStore } from '@/lib/desktop-shell/store';

const DesktopShellStoreContext = createContext<DesktopShellStore | null>(null);

/**
 * Provides the per-instance desktop shell store to the subtree so nested panes
 * and dialogs can read state directly instead of receiving it through props.
 */
export function DesktopShellStoreProvider({
  store,
  children,
}: {
  store: DesktopShellStore;
  children: ReactNode;
}) {
  return (
    <DesktopShellStoreContext.Provider value={store}>{children}</DesktopShellStoreContext.Provider>
  );
}

/** Returns the raw store instance from context (throws when used outside a provider). */
export function useDesktopShellStoreApi(): DesktopShellStore {
  const store = useContext(DesktopShellStoreContext);
  if (store === null) {
    throw new Error('useDesktopShellStore must be used within a DesktopShellStoreProvider.');
  }
  return store;
}

/** The store when the subtree has one (a desktop pane), null elsewhere (an embedded chat). */
export function useOptionalDesktopShellStoreApi(): DesktopShellStore | null {
  return useContext(DesktopShellStoreContext);
}

/** Subscribes to a slice of the desktop shell store using a selector. */
export function useDesktopShellStore<T>(selector: (state: DesktopShellState) => T): T {
  return useStore(useDesktopShellStoreApi(), selector);
}
